import { useDataTableContext } from "../../../contexts/DataTableContext";
import React, { useEffect, useMemo, useState } from "react";
import { Tree } from "react-arborist";
import AccessorTreeNode, { isConstituentTreeNode, TreeNode, TreeNodeSelection } from "./AccessorTreeNode";

import Button from "../Button/Button";
import Input from "../Input/Input";
import Case from "case";
import { Accessor, ColumnSelectionPreset, Constituent, PropertyAttribute } from "../../../types/types";
import { Dropdown, Message } from "semantic-ui-react";

interface ColumnTreeviewProps {
	onApplied: () => void;
}

const getInitialState = (all: string[], selectionInContext: string[]) => {
	return Object.fromEntries(all.map((accessor) => [accessor, selectionInContext.includes(accessor)]));
};

export default function AccessorTree({ onApplied }: ColumnTreeviewProps) {
	const {
		accessors,
		selectedAccessors,
		selectedPropertyAttributes,
		setSelectedAccessors,
		setSelectedPropertyAttributes,
		accessorsByConstituent,
		getFriendlyColumnName,
		propertyAttributes,
		isSelectColumnsModalOpen,
		columnSelectionPresets,
	} = useDataTableContext()!;

	const [selection, setSelection] = useState<Record<string, boolean>>(getInitialState(accessors, selectedAccessors));
	const [propertyAttributeSelection, setPropertyAttributeSelection] = useState<Record<string, boolean>>(
		getInitialState(propertyAttributes, selectedPropertyAttributes)
	);
	const [searchTerm, setSearchTerm] = useState("");
	const [presetSelected, setPresetSelected] = useState<string>("");

	useEffect(() => {
		if (!isSelectColumnsModalOpen) return;
		setPresetSelected("");
	}, [isSelectColumnsModalOpen]);

	const data = useMemo<TreeNode[]>(() => {
		// Also include the base property attributes
		const property = {
			id: "property",
			name: "Property",
			children: propertyAttributes.map((attribute) => ({
				id: attribute,
				name: getFriendlyColumnName(attribute),
				attribute,
			})),
		};

		const constituents = Object.entries(accessorsByConstituent).map(([constituent, accessors]) => ({
			id: constituent,
			name: Case.title(constituent),
			constituent: constituent as Constituent,
			children: accessors.map((accessor) => ({
				id: accessor,
				name: getFriendlyColumnName(accessor),
				accessor: accessor,
			})),
		}));

		return [property, ...constituents];
	}, [accessorsByConstituent, getFriendlyColumnName, propertyAttributes]);

	const numSelectedAccessors = useMemo(() => {
		const selectedAccessorCount = Object.values(selection).filter(Boolean).length;
		const selectedAttributeCount = Object.values(propertyAttributeSelection).filter(Boolean).length;
		return selectedAccessorCount + selectedAttributeCount;
	}, [propertyAttributeSelection, selection]);

	const presetOptions = useMemo(
		() =>
			columnSelectionPresets.map((preset) => ({
				key: String(preset.id),
				text: preset.name,
				value: String(preset.id),
			})),
		[columnSelectionPresets]
	);

	const getNodeSelection: (node: TreeNode) => TreeNodeSelection = (node) => {
		if ("accessor" in node) return selection[node.accessor] ? "all" : "none";
		if ("attribute" in node) return propertyAttributeSelection[node.attribute] ? "all" : "none";

		const allColumns = isConstituentTreeNode(node) ? accessorsByConstituent[node.constituent] : propertyAttributes;

		let numSelectedColumns = 0;
		for (const column of allColumns) {
			if (isConstituentTreeNode(node)) {
				// Convert to bool in case the column does not happen to exist.
				numSelectedColumns += Number(!!selection[column]);
			} else {
				numSelectedColumns += Number(!!propertyAttributeSelection[column]);
			}
		}

		if (numSelectedColumns === 0) return "none";

		if (numSelectedColumns === allColumns.length) return "all";

		return "some";
	};

	const onSearchInput = (e: React.FormEvent<HTMLInputElement>) => {
		setSearchTerm((e.target as HTMLInputElement).value);
	};

	const onSelectAll = () => {
		setSelection(Object.fromEntries(accessors.map((accessor) => [accessor, true])));
		setPropertyAttributeSelection(Object.fromEntries(propertyAttributes.map((attribute) => [attribute, true])));
		setPresetSelected("");
	};

	const onClearAccessors = () => {
		setSelection(Object.fromEntries(accessors.map((accessor) => [accessor, false])));
		setPropertyAttributeSelection(Object.fromEntries(propertyAttributes.map((attribute) => [attribute, false])));
		setPresetSelected("");
	};

	const applyPreset = (preset: ColumnSelectionPreset) => {
		const newAccessors: Accessor[] = [];
		const newAttributes: PropertyAttribute[] = [];
		for (const col of preset.selected_columns ?? []) {
			if (col.includes(".")) newAccessors.push(col as Accessor);
			else newAttributes.push(col as PropertyAttribute);
		}
		setSelection(Object.fromEntries(accessors.map((a) => [a, newAccessors.includes(a)])));
		setPropertyAttributeSelection(
			Object.fromEntries(propertyAttributes.map((a) => [a, newAttributes.includes(a)]))
		);
	};

	const onPresetChange = (menuValue: string | undefined) => {
		if (menuValue === undefined || menuValue === "") {
			setPresetSelected("");
			return;
		}
		const preset = columnSelectionPresets.find((p) => String(p.id) === menuValue);
		if (preset) applyPreset(preset);
		setPresetSelected(String(menuValue));
	};

	const onSelect = (node: TreeNode, selection: TreeNodeSelection) => {
		if ("accessor" in node) {
			setSelection((prev) => {
				const updated = { ...prev };
				updated[node.accessor] = selection === "none";
				return updated;
			});

			return;
		} else if ("attribute" in node) {
			setPropertyAttributeSelection((prev) => {
				const updated = { ...prev };
				updated[node.attribute] = selection === "none";
				return updated;
			});

			return;
		}

		if (isConstituentTreeNode(node)) {
			setSelection((prev) => {
				const updated = { ...prev };
				for (const accessor of accessorsByConstituent[node.constituent])
					updated[accessor] = selection !== "all";
				return updated;
			});
		} else {
			setPropertyAttributeSelection((prev) => {
				const updated = { ...prev };
				for (const accessor of propertyAttributes) updated[accessor] = selection !== "all";
				return updated;
			});
		}
	};

	const onConfirm = () => {
		const newAccessors = Object.keys(selection).filter(
			(key) => selection[key] && !selectedAccessors.includes(key as Accessor)
		) as Accessor[];
		const existingAccessors = selectedAccessors.filter((key) => selection[key]);
		// Order the accessors so that the new keys are placed at the end by default
		setSelectedAccessors([...existingAccessors, ...newAccessors]);

		// Same for property attributes
		const newAttributes = Object.keys(propertyAttributeSelection).filter(
			(key) => propertyAttributeSelection[key] && !selectedPropertyAttributes.includes(key as PropertyAttribute)
		) as PropertyAttribute[];
		const existingAttributes = selectedPropertyAttributes.filter((key) => propertyAttributeSelection[key]);
		setSelectedPropertyAttributes([...existingAttributes, ...newAttributes]);

		onApplied();
	};

	return (
		<>
			<div className="accessor-tree-toolbar">
				<div className="accessor-tree-toolbar-actions">
					<Button variant="outline" onClick={onSelectAll}>
						Select all
					</Button>
					<Button variant="outline" onClick={onClearAccessors}>
						Clear
					</Button>
				</div>
				{columnSelectionPresets.length > 0 && (
					<div className="accessor-tree-toolbar-presets">
						<Dropdown
							placeholder="Preset"
							selection
							clearable
							options={presetOptions}
							value={presetSelected}
							onChange={(_, { value }) => onPresetChange(value as string | undefined)}
						/>
					</div>
				)}
			</div>
			<Input placeholder="Search..." onInput={(e: React.FormEvent<HTMLInputElement>) => onSearchInput(e)} />
			<div className="accessors tree">
				<Tree
					initialData={data}
					openByDefault={false}
					disableDrag={true}
					disableMultiSelection={false}
					searchTerm={searchTerm}
					searchMatch={(node, term) => node.data.name.toLowerCase().includes(term.toLowerCase())}
					width={"100%"}
					height={window.innerHeight * 0.5}
				>
					{(props) => (
						<AccessorTreeNode
							selection={getNodeSelection(props.node.data)}
							onSelect={onSelect}
							{...props}
						/>
					)}
				</Tree>
			</div>
			<p>{numSelectedAccessors} column(s) selected</p>
			{numSelectedAccessors > 100 && (
				<Message warning>
					<p>Selecting a large number of columns could reduce performance.</p>
				</Message>
			)}
			<Button right variant="primary" className="confirm" onClick={onConfirm}>
				OK
			</Button>
		</>
	);
}
